import { cn } from "web-design-system";
import { useSunriseBannerDismissed } from "../sunrise-funds-banner";

type Filters = Record<string, string[] | string>;

// Each view applies a set of funds API filters
export const SMART_VIEWS: { id: string; label: string; filters: Filters }[] = [
  {
    id: "sunrise",
    label: "Sunrise funds",
    filters: { is_sunrise_fund: "true" },
  },
  { id: "top_ranked", label: "Top ranked", filters: { top_ranked: "true" } },
  { id: "favourites", label: "Favourites", filters: { favourites: "true" } },
  { id: "trending", label: "Trending", filters: { is_trending: "true" } },
];

export const isViewApplied = (filters: Filters, viewFilters: Filters) =>
  Object.entries(viewFilters).every(
    ([key, value]) => String(filters[key]) === String(value)
  );

export const removeViewFilters = (filters: Filters, viewFilters: Filters) => {
  const next = { ...filters };
  Object.keys(viewFilters).forEach((key) => delete next[key]);
  return next;
};

export function SmartViews({
  filters,
  onFiltersChange,
}: {
  filters: Filters;
  onFiltersChange: (update: (prev: Filters) => Filters) => void;
}) {
  const [, setSunriseBannerDismissed] = useSunriseBannerDismissed();

  // Clicking a view toggles its filters on top of whatever else is applied
  const toggleView = (viewId: string, viewFilters: Filters) => {
    // Picking Sunrise here makes the Sunrise banner redundant
    if (viewId === "sunrise" && !isViewApplied(filters, viewFilters)) {
      setSunriseBannerDismissed(true);
    }
    onFiltersChange((prev) => {
      if (isViewApplied(prev, viewFilters)) {
        return removeViewFilters(prev, viewFilters);
      }
      return { ...prev, ...viewFilters };
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <span className="text-sm font-semibold">Collections</span>
      <div className="grid grid-cols-2 gap-2">
        {SMART_VIEWS.map((view) => {
          const applied = isViewApplied(filters, view.filters);
          return (
            <button
              key={view.id}
              type="button"
              aria-pressed={applied}
              onClick={() => toggleView(view.id, view.filters)}
              className={cn(
                "rounded-lg border border-border px-3 py-2 text-left text-sm",
                applied && "border-primary bg-secondary font-medium"
              )}
            >
              {view.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
